import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that renders a card gallery for product cards.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLAnchorElement} [cardGalleryLink] - The link to the product.
 * @property {HTMLAnchorElement} [productTitleLink] - The product title link.
 *
 * @extends Component<Refs>
 */
export class CardGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('product-card');

    // Listen for variant updates from within the product card
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current card gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    
    // Find the card-gallery in the updated HTML
    const newCardGallery = source.querySelector('.card-gallery-default__media-wrapper');
    const currentCardGallery = this.querySelector('.card-gallery-default__media-wrapper');

    if (!newCardGallery || !currentCardGallery) return;

    // Some product-card section responses omit slider-specific badge markup.
    // Preserve the current badge container when the refreshed gallery does not include one.
    const currentBadges = currentCardGallery.querySelector('.product-card-badges, .card-gallery__badges-overlay');
    const newBadges = newCardGallery.querySelector('.product-card-badges, .card-gallery__badges-overlay');
    if (currentBadges && !newBadges) {
      newCardGallery.append(currentBadges.cloneNode(true));
    }

    currentCardGallery.replaceWith(newCardGallery);

    // Ref may have been replaced by morph; refresh slideshow ref
    const slideshowEl = this.querySelector('[ref="slideshow"]');
    if (slideshowEl) {
      this.refs.slideshow = /** @type {import('slideshow').Slideshow} */ (slideshowEl);
    }

    // Sync on:pointerenter / on:pointerleave from source card-gallery host; remove if not present
    const sourceHost = source.querySelector('card-gallery');
    const pointerEnter = 'on:pointerenter';
    const pointerLeave = 'on:pointerleave';
    if (sourceHost?.getAttribute(pointerEnter)) {
      this.setAttribute(pointerEnter, sourceHost.getAttribute(pointerEnter) ?? '');
    } else {
      this.removeAttribute(pointerEnter);
    }
    if (sourceHost?.getAttribute(pointerLeave)) {
      this.setAttribute(pointerLeave, sourceHost.getAttribute(pointerLeave) ?? '');
    } else {
      this.removeAttribute(pointerLeave);
    }
  };

  get slideshow() {
    return this.refs.slideshow;
  }

  get productId() {
    return this.dataset.productId;
  }

  /**
   * Delegates preview image to the parent product-card.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    const productCard = this.closest('product-card');
    if (productCard && typeof productCard.previewImage === 'function') {
      productCard.previewImage(event);
    }
  }

  /**
   * Delegates reset image to the parent product-card.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    const productCard = this.closest('product-card');
    if (productCard && typeof productCard.resetImage === 'function') {
      productCard.resetImage(event);
    }
  }
}

if (!customElements.get('card-gallery')) {
  customElements.define('card-gallery', CardGallery);
}
